import gsap from 'gsap';
import className from 'classnames/bind';
import { gql, useLazyQuery } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import ListingEventsFullItem from './Item';
import ScrollToPlugin from 'gsap/dist/ScrollToPlugin';
import { EventsByTaxonomy } from '@/queries/Events';
import { useHeader } from '@/providers/HeaderContext';
import { Container, Filters, MaterialLoader, QueryPagination, RevealWrapper, SectionHeader } from '@components';
import { EventsByTaxonomyQuery, GetEventsPageDataQuery, ListingEventsFullFragment } from '@graphqlTypes';

import { useViewSize } from '@/providers/ViewSizeContext';
import { AnimatePresence, useInView, motion } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';
import { useClientMount } from '@/providers/ClientMountProvider';

import styles from './ListingEventsFull.module.scss';
import { useRouter } from 'next/router';
import { usePageWrapperData } from '@/providers/PageWrapperContext';
import { useCalculatedScrollTrigger } from '@/hooks/calculatedScrollTrigger';
const cx = className.bind(styles);

type ListingEventsFullProps = ListingEventsFullFragment & {
  initialEvents?: GetEventsPageDataQuery['initialEvents'];
};

export default function ListingEventsFull(props: ListingEventsFullProps) {
  const { labels, eventTypes } = usePageWrapperData();
  const { headline, subhead, eventFilter, initialEvents } = props;
  const { mounted } = useClientMount();
  const { headerVisible, headerRef, setIgnoreScroll, currentLanguage } = useHeader();
  const contentRef = useRef<HTMLDivElement>(null);
  const hideFilterButtons = eventFilter?.length > 0;
  const headerVisibleRef = useRef(headerVisible);
  const baseFilters = eventFilter ? eventFilter.map((filter) => filter.slug) : [];
  const filterButtons = eventTypes?.nodes || [];
  const [typeFilters, setTypeFilters] = useState<string[]>(baseFilters);
  const prevTypeFilters = useRef<string[]>(baseFilters);
  const [events, setEvents] = useState<EventsByTaxonomyQuery['events']['nodes']>(initialEvents?.nodes ?? []);
  const [loading, setLoading] = useState(false);
  const [showPaging, setShowPaging] = useState(initialEvents?.pageInfo?.offsetPagination?.hasMore ?? false);
  const [totalCount, setTotalCount] = useState(initialEvents?.pageInfo?.totalCount ?? 0);
  const isFirstFetch = useRef(true);
  const didInitialFetch = useRef(false);
  const currentPage = useRef<number>(1);
  let PER_PAGE = 12;
  const { query } = useRouter();
  if (query.perPage && typeof query.perPage === 'string') {
    const parsed = parseInt(query.perPage, 10);
    if (!isNaN(parsed)) {
      PER_PAGE = parsed;
    }
  }
  const [getEvents] = useLazyQuery<EventsByTaxonomyQuery>(EventsByTaxonomy);

  const { isDesktop, isReady } = useViewSize();
  const rootEl = useRef<HTMLDivElement>(null);
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const { triggerAmount } = useCalculatedScrollTrigger(rootEl, triggerPercent, [events]);
  const isInView = useInView(rootEl, { amount: triggerAmount, once: REVEAL.once });

  const scrollToContent = () => {
    if (!mounted || !isReady) return;
    
    let offset = 24;
    if (headerVisibleRef.current && headerRef.current) offset += headerRef.current.offsetHeight;
    setIgnoreScroll(true);
    gsap.to(window, {
      scrollTo: { y: contentRef.current, offsetY: offset },
      onComplete: () => {
        setTimeout(() => { setIgnoreScroll(false); }, 150);
      }
    });
  };

  const handleFilterClick = (slug: string) => {
    setTypeFilters((prev) => {
      if (prev.includes(slug)) return prev.filter((s) => s !== slug);
      return [...prev, slug];
    });
  };

  const handlePageChange = (page: number) => {
    currentPage.current = page;
    fetchEvents();
  }

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    const filterChange = typeFilters.join(',') !== prevTypeFilters.current.join(',');
    if (!isFirstFetch.current) {
      scrollToContent();
    } else {
      isFirstFetch.current = false;
    }
    const { data } = await getEvents({
      variables: {
        offset: currentPage.current ? (currentPage.current - 1) * PER_PAGE : 0,
        language: currentLanguage,
        size: PER_PAGE,
        taxonomies: typeFilters
      }
    });
    if (data) {
      const { pageInfo: { totalCount, offsetPagination: { hasMore } }, nodes } = data.events;
      setEvents(nodes);
      if (filterChange) {
        setTotalCount(totalCount);
        setShowPaging(hasMore);
        prevTypeFilters.current = typeFilters;
      }
      if (hasMore && !showPaging) {
        setShowPaging(true);
        setTotalCount(totalCount);
      }
    }
    setLoading(false);
  }, [typeFilters]);

  useEffect(() => {
    if (!mounted || !isReady) return;
    gsap.registerPlugin(ScrollToPlugin);
  }, [mounted, isReady]);
  useEffect(() => {
    headerVisibleRef.current = headerVisible;
  }, [headerVisible]);
  useEffect(() => {
    currentPage.current = 1;
    const hasSeed = Boolean(initialEvents?.nodes?.length);
    const isDefaultFilters = typeFilters.length === 0;
    if (hasSeed && isDefaultFilters && PER_PAGE === 12 && !didInitialFetch.current) {
      didInitialFetch.current = true;
      isFirstFetch.current = false;
      return;
    }
    fetchEvents();
  }, [typeFilters]);
  return (
    <section className={cx(["listing-events-full"])} ref={rootEl}>
      <Container>
        <RevealWrapper isInView={isInView}>
          <SectionHeader
            headline={headline}
            subhead={subhead}
            isRevealEnabled={true}
            isInView={isInView}
          />
          <div className={cx("content", { loading })} ref={contentRef}>
            <AnimatePresence>
              {loading && (
                <motion.div className={cx("listing-events-full__loading")}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <MaterialLoader className={cx("listing-events-full__loading__loader")} />
                </motion.div>
              )}
            </AnimatePresence>
            {filterButtons.length && !hideFilterButtons && (
              <Filters
                filterButtons={filterButtons}
                typeFilters={typeFilters}
                handleFilterClick={handleFilterClick}
              />
            )}
            <div className={cx("listing-events-full__list")}>
              {events.map((event) => (
                <ListingEventsFullItem key={event.databaseId} {...event} />
              ))}
              {events.length === 0 && !loading && (
                <div className={cx("listing-events-full__empty")}>
                  <p className='title-lc-s'>{labels?.noEventsLabel}</p>
                </div>
              )}
            </div>
            {showPaging && (
              <QueryPagination
                className={cx("listing-events-full__pagination")}
                totalCount={totalCount}
                perPage={PER_PAGE}
                currentPage={currentPage.current}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </RevealWrapper>
      </Container>
    </section>
  )
}

ListingEventsFull.fragments = {
  entry: gql`
    fragment ListingEventsFull on FlexiblePageContentContentListingEventsFullLayout {
      headline
      subhead
      eventFilter {
        databaseId
        slug
      }
    }
  `,
};